// store global types
import type { TCreateSlice, TAsyncStateWithProgressMessage } from "@/store";

// dependencies types
import type { TTableLocation, TFileUploadResponse } from "@/apis/odin/apiODIN";
import { Userpilot } from "userpilot";

type TExtractionId = number;

type TSuccessPayload = {
    extractionId: TExtractionId;
    allFoundTables: TTableLocation[];
};

type TActionsPrivateUploadFile = {
    setFileId: (payload: number) => void;
    setFileExists: (payload: boolean) => void;
    startUploading: () => void;
    uploadProgress: (payload: {
        progress: number;
        progressMessage: string;
    }) => void;
    uploadSuccess: (params: TSuccessPayload) => void;
    uploadFailure: (payload: TUploadError) => void;
    checkFileValidity: ({
        files,
        rentRollEnabled,
    }: {
        files: FileList;
        rentRollEnabled: boolean;
    }) => File;
    addDealFile: (params: {
        projectId: number;
        databaseId: number;
        file: File | Blob;
        addDealFileFn: (params: {
            projectId: number;
            databaseId: number;
            file: File | Blob;
        }) => Promise<TFileUploadResponse>;
    }) => Promise<void>;
};
type TActionsPublicUploadFile = {
    setStep: (payload: "upload" | "documentType" | "extract") => void;
    resetStateUploadFile: () => void;
    setDocumentType: (payload: "operating_statement" | "rent_roll") => void;
    setFileName: (payload: string) => void;
    uploadFile: (params: {
        files: FileList;
        projectId: number;
        databaseId: number;
        addDealFile: (params: {
            projectId: number;
            databaseId: number;
            file: File | Blob;
        }) => Promise<TFileUploadResponse>;
    }) => Promise<void>;
};

type TUploadError = {
    errorMessage: string;
    isNetworkError?: boolean;
};
type TStateUploadFile = {
    step: "upload" | "documentType" | "extract";
    documentType: "operating_statement" | "rent_roll" | null;
    fileName: string | null;
    fileId: number | null;
    fileExists: boolean;
    uploadState: TAsyncStateWithProgressMessage<TSuccessPayload, TUploadError>;
};

type TSliceUploadFileParams = {
    getRentrollUploadEnabled: () => boolean;
};

export type TSliceUploadFile = TStateUploadFile & TActionsPublicUploadFile;

export const initialStateUploadFile = {
    step: "upload",
    documentType: null,
    fileName: null,
    fileId: null,
    fileExists: false,
    uploadState: {
        status: "idle" as const,
        progress: null,
        progressMessage: null,
        error: null,
        data: null,
    },
} satisfies TStateUploadFile;

export const createSliceUploadFile: TCreateSlice<
    TSliceUploadFile,
    TSliceUploadFileParams
> =
    ({ getRentrollUploadEnabled }) =>
    (set, get) => {
        const actionsPrivate: TActionsPrivateUploadFile = {
            setFileId: payload =>
                set(
                    state => ({ ...state, fileId: payload }),
                    false,
                    "uploadFile/setFileId",
                ),
            setFileExists: payload =>
                set(
                    state => ({ ...state, fileExists: payload }),
                    false,
                    "uploadFile/setFileExists",
                ),
            startUploading: () =>
                set(
                    state => ({
                        ...state,
                        uploadState: {
                            ...state.uploadState,
                            data: null,
                            error: null,
                            status: "pending",
                        },
                    }),
                    false,
                    "uploadFile/startUploading",
                ),
            uploadProgress: payload =>
                set(
                    state => ({
                        ...state,
                        uploadState: {
                            ...state.uploadState,
                            progress: payload.progress,
                            progressMessage: payload.progressMessage,
                        },
                    }),
                    false,
                    "uploadFile/uploadProgress",
                ),
            uploadSuccess: payload =>
                set(
                    state => ({
                        ...state,
                        uploadState: {
                            ...state.uploadState,
                            status: "resolved",
                            error: null,
                            data: payload,
                        },
                    }),
                    false,
                    "uploadFile/uploadSuccess",
                ),
            uploadFailure: payload => {
                set(
                    state => ({
                        ...state,
                        uploadState: {
                            ...state.uploadState,
                            status: "rejected",
                            error: payload,
                            data: null,
                            progress: null,
                            progressMessage: null,
                        },
                    }),
                    false,
                    "uploadFile/uploadFailure",
                );
            },
            checkFileValidity: ({
                files,
                rentRollEnabled,
            }: {
                files: FileList;
                rentRollEnabled: boolean;
            }): File => {
                let supportedFileTypes = [
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/pdf",
                    "application/vnd.ms-excel.sheet.macroenabled.12", // .xlsm
                    "application/vnd.ms-excel",
                ];

                if (rentRollEnabled) {
                    supportedFileTypes = [
                        ...supportedFileTypes,
                        "text/csv",
                        "application/vnd.ms-excel.sheet.binary.macroenabled.12", // .xlsb
                    ];
                }

                if (files.length > 1) {
                    throw new Error(
                        "Only one file is allowed. Please select a single Excel file or PDF.",
                    );
                }

                const file = files[0];
                if (!file) {
                    throw new Error("No file selected.");
                }

                if (
                    !file.type ||
                    supportedFileTypes.indexOf(file.type.toLowerCase()) === -1
                ) {
                    throw new Error(
                        "It looks like this file type isn\u2019t supported.",
                    );
                }
                return file;
            },
            addDealFile: async ({
                projectId,
                databaseId,
                file,
                addDealFileFn,
            }) => {
                try {
                    const result = await addDealFileFn({
                        projectId,
                        databaseId,
                        file,
                    });
                    if (result.error !== null && result.error !== undefined) {
                        actionsPrivate.uploadFailure({
                            errorMessage: `Error: ${result.error}`,
                        });
                        return;
                    }
                    actionsPrivate.setFileId(result.fileId);
                    actionsPrivate.setFileExists(result.existing ?? false);
                    if (file instanceof File) {
                        actionsPublic.setFileName(file.name);
                    }
                } catch (error) {
                    actionsPrivate.uploadFailure({
                        errorMessage: (error as Error).message,
                        isNetworkError: error instanceof TypeError,
                    });
                }
            },
        };
        const actionsPublic: TActionsPublicUploadFile = {
            resetStateUploadFile: () =>
                set(
                    { ...initialStateUploadFile },
                    false,
                    "uploadFile/resetStateUploadFile",
                ),
            setStep: (payload: "upload" | "documentType" | "extract") => {
                set(
                    state => ({ ...state, step: payload }),
                    false,
                    "uploadFile/setStep",
                );
            },
            setDocumentType: payload =>
                set(
                    state => ({ ...state, documentType: payload }),
                    false,
                    "uploadFile/setDocumentType",
                ),
            setFileName: payload =>
                set(
                    state => ({ ...state, fileName: payload }),
                    false,
                    "uploadFile/setFileName",
                ),
            uploadFile: async ({
                files,
                projectId,
                databaseId,
                addDealFile,
            }: {
                files: FileList;
                projectId: number;
                databaseId: number;
                addDealFile: (params: {
                    projectId: number;
                    databaseId: number;
                    file: File | Blob;
                }) => Promise<TFileUploadResponse>;
            }) => {
                let file: File;
                Userpilot.track("odin_user_attempts_file_upload");
                actionsPublic.setStep("upload");
                const rentRollEnabled = getRentrollUploadEnabled();
                try {
                    file = actionsPrivate.checkFileValidity({
                        files,
                        rentRollEnabled,
                    });
                } catch (error) {
                    Userpilot.track("odin_file_upload_fails_invalid_document");
                    actionsPrivate.uploadFailure({
                        errorMessage: (error as Error).message,
                    });
                    return;
                }
                if (!projectId) {
                    actionsPrivate.uploadFailure({
                        errorMessage: "Error: Project ID is missing.",
                    });
                    Userpilot.track("odin_file_upload_fails_other");
                    return;
                }

                actionsPrivate.startUploading();

                await actionsPrivate.addDealFile({
                    projectId,
                    databaseId,
                    file,
                    addDealFileFn: addDealFile,
                });
                if (get().uploadState.error !== null) {
                    return;
                }
                const fileId = get().fileId;
                if (fileId === null) {
                    Userpilot.track("odin_file_upload_fails_other");
                    actionsPrivate.uploadFailure({
                        errorMessage: "Error: The file could not be uploaded.",
                    });
                    return;
                }
                if (!rentRollEnabled) {
                    actionsPublic.setStep("extract");
                    actionsPrivate.uploadSuccess({
                        extractionId: 0,
                        allFoundTables: [],
                    });
                    return;
                }
                actionsPrivate.uploadSuccess({
                    extractionId: 0,
                    allFoundTables: [],
                });
                actionsPublic.setStep("documentType");
            },
        };

        return {
            ...initialStateUploadFile,
            ...actionsPublic,
        };
    };
